import { useState } from 'react'
import { Dialog } from '../ui/Dialog'
import { DestinationPicker } from '../ui/DestinationPicker'
import { useCommand } from '../state/useCommand'
import { TelephonyCommandNames } from '../contract'
import type { CallRow } from '../state/store'
import { prettyNumber } from './_shared'

type Mode = 'blind' | 'attended' | 'redirect'
const CMD = {
  blind: TelephonyCommandNames.BlindTransfer,
  attended: TelephonyCommandNames.AttendedTransfer,
  redirect: TelephonyCommandNames.Redirect
} as const

// Transfer / redirect a live call. Replaces the old empty-destination blind transfer with a
// real target picker (roster pick or free type) and blind / attended / redirect modes.
export function TransferDialog({ call, onClose }: { call: CallRow; onClose: () => void }) {
  const run = useCommand()
  const [mode, setMode] = useState<Mode>('blind')
  const [dest, setDest] = useState('')
  const who = call.callerIdName || prettyNumber(call.callerIdNumber) || call.channel

  const submit = async () => {
    const d = dest.trim()
    if (!d) return
    const res = await run(
      { command: CMD[mode], params: { channel: call.channel, destination: d, context: 'from-internal' } },
      { successMessage: `${mode === 'redirect' ? 'Redirected' : 'Transferred'} ${who} → ${d}` }
    )
    if (res && res.status === 'success') onClose()
  }

  return (
    <Dialog
      title={`Transfer — ${who}`}
      onClose={onClose}
      footer={
        <>
          <button className="sb-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="sb-btn-primary" disabled={!dest.trim()} onClick={() => void submit()}>
            {mode === 'attended' ? 'Start attended' : mode === 'redirect' ? 'Redirect' : 'Transfer'}
          </button>
        </>
      }
    >
      <div className="sb-seg" role="tablist" aria-label="Transfer mode">
        <button role="tab" aria-selected={mode === 'blind'} className={mode === 'blind' ? 'on' : ''} onClick={() => setMode('blind')}>Blind</button>
        <button role="tab" aria-selected={mode === 'attended'} className={mode === 'attended' ? 'on' : ''} onClick={() => setMode('attended')}>Attended</button>
        <button role="tab" aria-selected={mode === 'redirect'} className={mode === 'redirect' ? 'on' : ''} onClick={() => setMode('redirect')}>Redirect</button>
      </div>
      <DestinationPicker value={dest} onChange={setDest} exclude={call.extension} />
      <p className="sb-note">
        {mode === 'blind' && 'Blind transfer hands the caller straight to the destination.'}
        {mode === 'attended' && 'Attended transfer dials the destination so you can announce the caller first.'}
        {mode === 'redirect' && 'Redirect moves this channel to the destination in the from-internal context.'}
      </p>
    </Dialog>
  )
}
